/*
 * The admin route table (flows/admin-auth). Two public screens (login, forced reset) sit outside the
 * guard; everything else nests under <RequireAuth />. Real ops screens replace the ProtectedSection
 * placeholder as their slices land — Configuration is the first (CAE-300), still wrapped so the
 * config:view permission gate applies to deep links.
 */
import { Navigate, type RouteObject } from 'react-router-dom';
import { LoginScreen } from './screens/LoginScreen';
import { ResetPasswordScreen } from './screens/ResetPasswordScreen';
import { CatalogueRoute } from './screens/CatalogueRoute';
import { ConfigurationScreen } from './screens/ConfigurationScreen';
import { ContestsListScreen } from './screens/ContestsListScreen';
import { ContestDetailScreen } from './screens/ContestDetailScreen';
import { ContestEditorScreen } from './screens/ContestEditorScreen';
import { PlayersScreen } from './screens/PlayersScreen';
import { PlayerDetailScreen } from './screens/PlayerDetailScreen';
import { EntryDetailScreen } from './screens/EntryDetailScreen';
import { AwardsScreen } from './screens/AwardsScreen';
import { SettlementScreen } from './screens/SettlementScreen';
import { AdminsScreen } from './screens/AdminsScreen';
import { RequireAuth } from './shell/RequireAuth';
import { ProtectedSection } from './shell/ProtectedSection';
import { LANDING_SECTION, NAV_SECTIONS } from './shell/nav-sections';

function sectionElement(sectionKey: string) {
  if (sectionKey === 'catalogue') {
    return <CatalogueRoute />;
  }
  if (sectionKey === 'configuration') {
    return (
      <ProtectedSection sectionKey="configuration">
        <ConfigurationScreen />
      </ProtectedSection>
    );
  }
  if (sectionKey === 'contests') {
    return (
      <ProtectedSection sectionKey="contests">
        <ContestsListScreen />
      </ProtectedSection>
    );
  }
  if (sectionKey === 'players') {
    return (
      <ProtectedSection sectionKey="players">
        <PlayersScreen />
      </ProtectedSection>
    );
  }
  if (sectionKey === 'awards') {
    return (
      <ProtectedSection sectionKey="awards">
        <AwardsScreen />
      </ProtectedSection>
    );
  }
  if (sectionKey === 'settlement') {
    return (
      <ProtectedSection sectionKey="settlement">
        <SettlementScreen />
      </ProtectedSection>
    );
  }
  if (sectionKey === 'admins') {
    return (
      <ProtectedSection sectionKey="admins">
        <AdminsScreen />
      </ProtectedSection>
    );
  }
  return <ProtectedSection sectionKey={sectionKey} />;
}

export const routes: RouteObject[] = [
  { path: '/login', element: <LoginScreen /> },
  { path: '/reset-password', element: <ResetPasswordScreen /> },
  {
    element: <RequireAuth />,
    children: [
      ...NAV_SECTIONS.map((section) => ({
        path: section.path,
        element: sectionElement(section.key),
      })),
      // Contest detail and the editor are nested under the landing section's permission gate
      // (contests:view) but are not themselves nav sections, so they sit outside the NAV_SECTIONS map.
      {
        path: '/contests/:id',
        element: (
          <ProtectedSection sectionKey="contests">
            <ContestDetailScreen />
          </ProtectedSection>
        ),
      },
      {
        path: '/contests/:id/edit',
        element: (
          <ProtectedSection sectionKey="contests">
            <ContestEditorScreen />
          </ProtectedSection>
        ),
      },
      // The support flow's drill-downs. Neither is a nav section, so both sit outside the
      // NAV_SECTIONS map — but each is gated on the section it is reached through: a player and
      // their entries are both `users:view` (API-21/API-22).
      {
        path: '/players/:id',
        element: (
          <ProtectedSection sectionKey="players">
            <PlayerDetailScreen />
          </ProtectedSection>
        ),
      },
      {
        path: '/entries/:id',
        element: (
          <ProtectedSection sectionKey="players">
            <EntryDetailScreen />
          </ProtectedSection>
        ),
      },
      { path: '*', element: <Navigate to={LANDING_SECTION.path} replace /> },
    ],
  },
];
